import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import {
  BranchCommitsContextDto,
  IBranchDto,
  IBranchUpdateModel,
  CommitStagedResponseDto,
  IDeactivateBranchingResultDto,
  IDeleteBranchResultDto,
  IMergeRequestDto,
  IMergeRequestItemDto,
  IMergeResultDto,
  StagedEntity,
  StagingStatusDto,
  RestoreSnapshotRequestDto,
  RevertToPointRequestDto
} from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

type MergeIntentListener = () => void;

class BranchingService {
  private static pendingMergeIntent = false;
  private static mergeIntentListeners: Set<MergeIntentListener> = new Set();

  private static async apiCall<T>(request: Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
    try {
      return await request;
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  public static async InitializeBranching(): Promise<AxiosResponse<IBranchDto>> {
    return this.apiCall(Axios.post<IBranchDto>(`${backendUrl}/branching/initializeBranching`));
  }

  public static async GetAllBranches(): Promise<AxiosResponse<IBranchDto[]>> {
    return this.apiCall(Axios.get<IBranchDto[]>(`${backendUrl}/branching/getall`));
  }

  public static async IsBranchingActive(): Promise<AxiosResponse<boolean>> {
    return this.apiCall(Axios.get<boolean>(`${backendUrl}/branching/isActive`));
  }

  public static async EnableStagingOnly(): Promise<AxiosResponse<StagingStatusDto>> {
    return this.apiCall(Axios.post<StagingStatusDto>(`${backendUrl}/branching/enableStagingOnly`));
  }

  public static async GetStagedDraftEntities(projectId: string): Promise<AxiosResponse<StagedEntity[]>> {
    return this.apiCall(
      Axios.get<StagedEntity[]>(`${backendUrl}/branching/stagedDraftEntities`, { params: { projectId } })
    );
  }

  /**
   * OpIds only — no before/after snapshots. Chat rewind baseline must use this
   * so every send is not gated on the heavy stagedDraftEntities materialization.
   *
   * Does NOT use apiCall / exceptionHandler: timeout or missing route is an
   * expected soft-fail (caller falls back to GetStagedDraftEntities). Toasts
   * for ECONNABORTED would spam the UI on every slow project.
   */
  public static async GetStagedDraftOpIds(projectId: string): Promise<AxiosResponse<string[]>> {
    return Axios.get<string[]>(`${backendUrl}/branching/stagedDraftOpIds`, {
      params: { projectId },
      // Hard ceiling: baseline must complete before tools run, but must not hang unboundedly.
      timeout: 15_000
    });
  }

  public static async DiscardDraftStagedOps(opIds: string[], projectId: string): Promise<AxiosResponse<boolean>> {
    return this.apiCall(
      Axios.post<boolean>(`${backendUrl}/branching/stagedDraft/ops/discard`, opIds, { params: { projectId } })
    );
  }

  public static async UpdateUserBranchingState(branchId: string): Promise<AxiosResponse<boolean>> {
    return this.apiCall(Axios.post<boolean>(`${backendUrl}/branching/updateUserBranchingState/${branchId}`));
  }

  public static async CreateBranch(
    branch: Pick<IBranchDto, "name" | "parentBranchId">
  ): Promise<AxiosResponse<IBranchDto>> {
    return this.apiCall(Axios.post<IBranchDto>(`${backendUrl}/branching/create`, branch));
  }

  public static async RenameBranch(branchInfo: IBranchUpdateModel): Promise<AxiosResponse<IBranchDto>> {
    return this.apiCall(Axios.post<IBranchDto>(`${backendUrl}/branching/rename`, branchInfo));
  }

  public static async GetStagedEntities(branchId: string): Promise<AxiosResponse<StagedEntity[]>> {
    return this.apiCall(Axios.get<StagedEntity[]>(`${backendUrl}/branching/stagedEntities/${branchId}`));
  }

  public static async ApplyStagedOps(branchId: string, opIds: string[]): Promise<AxiosResponse<boolean>> {
    return this.apiCall(Axios.post<boolean>(`${backendUrl}/branching/staged/${branchId}/ops/apply`, opIds));
  }

  public static async CommitStaged(branchId: string, message: string): Promise<AxiosResponse<CommitStagedResponseDto>> {
    return this.apiCall(
      Axios.post<CommitStagedResponseDto>(`${backendUrl}/branching/staged/${branchId}/commit`, { message })
    );
  }

  public static async DiscardStagedOps(branchId: string, opIds: string[]): Promise<AxiosResponse<boolean>> {
    return this.apiCall(Axios.post<boolean>(`${backendUrl}/branching/staged/${branchId}/ops/discard`, opIds));
  }

  public static async GetCommitsContext(branchId: string, take = 100): Promise<AxiosResponse<BranchCommitsContextDto>> {
    return this.apiCall(
      Axios.get<BranchCommitsContextDto>(`${backendUrl}/branching/commitsContext/${branchId}?take=${take}`)
    );
  }

  public static async RevertToPoint(
    branchId: string,
    payload: RevertToPointRequestDto
  ): Promise<AxiosResponse<boolean>> {
    return this.apiCall(Axios.post<boolean>(`${backendUrl}/branching/revert/toPoint/${branchId}`, payload));
  }

  public static async RestoreSnapshot(
    branchId: string,
    payload: RestoreSnapshotRequestDto
  ): Promise<AxiosResponse<boolean>> {
    return this.apiCall(Axios.post<boolean>(`${backendUrl}/branching/revert/restoreSnapshot/${branchId}`, payload));
  }

  public static async MergeBranch(mergeData: IMergeRequestDto): Promise<AxiosResponse<IMergeResultDto>> {
    return this.apiCall(Axios.post<IMergeResultDto>(`${backendUrl}/branching/mergeBranch`, mergeData));
  }

  public static async ResolveMergeConflicts(mergeData: IMergeRequestDto): Promise<AxiosResponse<IMergeResultDto>> {
    return this.apiCall(Axios.post<IMergeResultDto>(`${backendUrl}/branching/resolveMergeConflicts`, mergeData));
  }

  public static async GetMergeRequests(pendingOnly = false): Promise<AxiosResponse<IMergeRequestItemDto[]>> {
    return this.apiCall(
      Axios.get<IMergeRequestItemDto[]>(`${backendUrl}/branching/mergeRequests`, { params: { pendingOnly } })
    );
  }

  public static async GetMergeRequestChanges(mergeRequestId: string): Promise<AxiosResponse<StagedEntity[]>> {
    return this.apiCall(Axios.get<StagedEntity[]>(`${backendUrl}/branching/mergeRequests/${mergeRequestId}/changes`));
  }

  public static async ApproveMergeRequest(mergeRequestId: string): Promise<AxiosResponse<IMergeResultDto>> {
    return this.apiCall(
      Axios.post<IMergeResultDto>(`${backendUrl}/branching/mergeRequests/${mergeRequestId}/approve`, {})
    );
  }

  public static async ResolveMergeRequestConflicts(
    mergeRequestId: string,
    mergeData: IMergeRequestDto
  ): Promise<AxiosResponse<IMergeResultDto>> {
    return this.apiCall(
      Axios.post<IMergeResultDto>(`${backendUrl}/branching/mergeRequests/${mergeRequestId}/resolve`, mergeData)
    );
  }

  public static async RejectMergeRequest(mergeRequestId: string, comment: string): Promise<AxiosResponse<boolean>> {
    return this.apiCall(
      Axios.post<boolean>(`${backendUrl}/branching/mergeRequests/${mergeRequestId}/reject`, { comment })
    );
  }

  public static async CancelMergeRequest(mergeRequestId: string): Promise<AxiosResponse<boolean>> {
    return this.apiCall(Axios.post<boolean>(`${backendUrl}/branching/mergeRequests/${mergeRequestId}/cancel`, {}));
  }

  public static async SetBranchProtection(branchId: string, isProtected: boolean): Promise<AxiosResponse<boolean>> {
    return this.apiCall(
      Axios.post<boolean>(`${backendUrl}/branching/branches/${branchId}/protection`, { isProtected })
    );
  }

  public static async CancelMerge(mergeResult: IMergeResultDto): Promise<AxiosResponse<boolean>> {
    return this.apiCall(
      Axios.post<boolean>(`${backendUrl}/branching/cancelMerge`, mergeResult, {
        headers: {
          "Content-Type": "application/json"
        }
      })
    );
  }

  public static async DeactivateBranching(): Promise<AxiosResponse<IDeactivateBranchingResultDto>> {
    return this.apiCall(Axios.post<IDeactivateBranchingResultDto>(`${backendUrl}/branching/deactivate`));
  }

  public static async DeleteBranch(branchId: string): Promise<AxiosResponse<IDeleteBranchResultDto>> {
    return this.apiCall(Axios.post<IDeleteBranchResultDto>(`${backendUrl}/branching/delete/${branchId}`));
  }

  public static RequestStartMerge(): void {
    if (this.mergeIntentListeners.size > 0) {
      this.mergeIntentListeners.forEach((listener) => listener());
      return;
    }
    this.pendingMergeIntent = true;
  }

  public static SubscribeMergeIntent(listener: MergeIntentListener): () => void {
    this.mergeIntentListeners.add(listener);
    if (this.pendingMergeIntent) {
      this.pendingMergeIntent = false;
      listener();
    }
    return () => {
      this.mergeIntentListeners.delete(listener);
    };
  }
}

export default BranchingService;
